import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useManagerTheme } from '../context/ManagerThemeContext';
import LogoutModal from './LogoutModal';
import ManagerTransferModal from './ManagerTransferModal';

interface ManagerSidebarProps {
  activeTab?: string;
  onNavigation?: (tabId: string) => void;
  showHeader?: boolean;
  className?: string;
}

const ManagerSidebar: React.FC<ManagerSidebarProps> = ({
  activeTab = 'home',
  onNavigation,
  showHeader = false,
  className = '',
}) => {
  const { user, logout } = useAuth();
  const { sidebarColor } = useManagerTheme();
  const [currentActiveTab, setCurrentActiveTab] = useState(activeTab);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  useEffect(() => {
    setCurrentActiveTab(activeTab);
  }, [activeTab]);

  const sidebarItems = [
    { id: 'home', label: 'Home', icon: '🏠', path: '/manager' },
    { id: 'account', label: 'My Account', icon: '👤', path: '/manager/account' },
    { id: 'payouts', label: 'Payouts', icon: '💰', path: '/manager/payouts' },
    { id: 'reports', label: 'Reports', icon: '📊', path: '/manager/reports' },
    { id: 'merchants', label: 'My Merchants', icon: '🏪', path: '/manager/merchants' },
    { id: 'credit', label: 'Merchant Reports', icon: '💳', path: '/manager/merchant-report-history' },
    { id: 'settings', label: 'Settings', icon: '⚙️', path: '/manager/settings' },
  ];

  const handleNavigation = (tabId: string, path: string) => {
    setCurrentActiveTab(tabId);
    if (onNavigation) {
      onNavigation(tabId);
    } else {
      window.location.href = path;
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleCreditMoney = () => setShowTransferModal(true);
  const handleMyProfile = () => (window.location.href = '/manager/account');

  const navItemBase =
    'flex items-center gap-4 w-full py-3.5 px-5 rounded-xl text-left text-sm font-medium transition-all duration-200 cursor-pointer border-none border-l-4 border-l-transparent';
  const navItemInactive =
    'text-slate-300 hover:text-white hover:bg-white/10';
  const navItemActive =
    'text-white font-semibold bg-violet-500/25 border-l-violet-400 shadow-inner';

  return (
    <div className={`flex h-full min-h-screen min-h-0 ${className}`} style={{ backgroundColor: sidebarColor }}>
      {/* Sidebar - own column; no overlap with main content */}
      <aside className="w-full flex-shrink-0 flex flex-col min-h-full min-h-0 relative border-r border-slate-700/80 shadow-[4px_0_24px_rgba(0,0,0,0.4)] overflow-hidden" style={{ backgroundColor: sidebarColor }}>
        {/* Branding */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-700/80 shrink-0">
          <div className="w-10 h-10 rounded-xl bg-violet-500/30 flex items-center justify-center text-white shadow-lg border border-white/10">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
            </svg>
          </div>
          <span className="text-lg font-bold text-white tracking-tight">Manager Panel</span>
        </div>

        {showHeader && (
          <div className="p-6 border-b border-slate-700/80 bg-white/5 shrink-0">
            <img
              src={`${process.env.PUBLIC_URL || ''}/logo.png`}
              alt="Logo"
              className="w-full max-w-[180px] h-auto object-contain"
            />
          </div>
        )}

        <nav className="shrink-0 py-4 px-3 overflow-x-hidden">
          {sidebarItems.map((item) => {
            const isActive = currentActiveTab === item.id;
            return (
              <button
                key={item.id}
                className={`${navItemBase} mb-1 ${isActive ? navItemActive : navItemInactive}`}
                onClick={() => handleNavigation(item.id, item.path)}
              >
                <span className={`text-xl min-w-[28px] w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${isActive ? 'bg-violet-400/20 text-white' : 'text-slate-400'}`}>
                  {item.icon}
                </span>
                <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">
                  {item.label}
                </span>
              </button>
            );
          })}
          {/* Credit Money & Logout - right after Settings */}
          <div className="mt-2 pt-2 border-t border-slate-700/80 space-y-2">
            <button
              type="button"
              className="flex items-center gap-3 w-full py-3.5 px-5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500 shadow-lg hover:shadow-violet-500/25 transition-all duration-200 border-none cursor-pointer mb-1"
              onClick={handleCreditMoney}
            >
              <span className="text-lg">💳</span>
              <span>Credit Money</span>
            </button>
            <button
              type="button"
              className="flex items-center gap-3 w-full py-3.5 px-5 rounded-xl text-sm font-semibold text-slate-200 bg-white/5 hover:bg-red-500/20 hover:text-red-200 border border-slate-600/50 hover:border-red-500/50 transition-all duration-200 cursor-pointer"
              onClick={handleLogoutClick}
            >
              <span className="text-lg">🚪</span>
              <span>Logout</span>
            </button>
          </div>
        </nav>

        {/* Spacer so footer sits at bottom of sidebar */}
        <div className="flex-1 min-h-0" aria-hidden />

        <div className="shrink-0 p-3 pt-2 pb-3 border-t border-slate-700/80">
          <p className="text-center text-xs text-slate-500 pt-1 pb-0 m-0">Manager Portal</p>
        </div>
      </aside>

      {showHeader && (
        <div className="flex justify-between items-center h-20 min-h-[80px] px-8 py-6 bg-white border-b border-slate-200 shadow-sm fixed top-0 right-0 z-[999] ml-[250px] w-[calc(100vw-250px)] box-border">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800 m-0">
              Hi {user?.name || 'Manager'}, Welcome to YuvanApi!
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="flex items-center gap-2 py-3 px-5 rounded-lg text-sm font-semibold bg-slate-100 text-violet-600 border-2 border-slate-200 hover:bg-slate-200 hover:border-violet-600 hover:-translate-y-0.5 transition-all"
              onClick={() => window.location.reload()}
            >
              <span>🔄</span> WALLET BALANCE
            </button>
            <button
              type="button"
              className="flex items-center gap-2 py-3 px-5 rounded-lg text-sm font-semibold text-white border-none shadow-md hover:-translate-y-0.5 hover:shadow-lg transition-all"
              style={{
                background: 'linear-gradient(135deg, #6c5ce7 0%, #5a4fcf 100%)',
                boxShadow: '0 4px 12px rgba(108, 92, 231, 0.3)',
              }}
              onClick={handleCreditMoney}
            >
              <span>💳</span> CREDIT MONEY
            </button>
            <button
              type="button"
              className="flex items-center gap-2 py-3 px-5 rounded-lg text-sm font-semibold bg-slate-100 text-violet-600 border-2 border-slate-200 hover:bg-slate-200 hover:border-violet-600 hover:-translate-y-0.5 transition-all"
              onClick={handleMyProfile}
            >
              <span>👤</span> My Profile
            </button>
            <button
              type="button"
              className="flex items-center gap-2 py-3 px-5 rounded-lg text-sm font-semibold text-white border-none shadow-md hover:-translate-y-0.5 transition-all focus:outline-2 focus:outline-offset-2 focus:outline-blue-400"
              style={{
                background: 'linear-gradient(135deg, #e17055 0%, #d63031 100%)',
                boxShadow: '0 4px 12px rgba(214, 48, 49, 0.3)',
              }}
              onClick={handleLogoutClick}
            >
              <span>🚪</span> Logout
            </button>
          </div>
        </div>
      )}

      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        userName={user?.name}
        userRole={user?.role}
      />
      <ManagerTransferModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        onTransferSuccess={() => window.location.reload()}
      />
    </div>
  );
};

export default ManagerSidebar;
